import {
  ChevronDownIcon,
  PencilSquareIcon,
  UserPlusIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useState, useCallback, useEffect } from 'react';
import { useAdminStore } from '@/store/admin';
import { Avatar } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { deleteDepartment } from '@mygpt/app';
import MemberTransferModal from './MemberTransferModal';
import { useTranslation } from 'react-i18next';
import DeleteConfirmModal from './DeleteConfirmModal';

interface DepartmentTableProps {
  onAddDepartment: (parentId: string) => void;
  onEditDepartment: (dept: any) => void;
  onEditCompany: () => void;
}

export default function DepartmentTable({ onAddDepartment, onEditDepartment, onEditCompany }: DepartmentTableProps) {
  const { companyId, fetchDepartmentByCompanyId, companyName, departmentList, companyDescription } = useAdminStore(
    (state) => state
  );
  const { refreshDepartmentData } = useAdminStore((state) => state);
  const { t } = useTranslation();

  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set(['company-root']));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<string | null>(null);

  const toggleDepartment = useCallback((deptId: string) => {
    setExpandedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(deptId)) {
        next.delete(deptId);
      } else {
        next.add(deptId);
      }
      return next;
    });
  }, []);

  const handleAddMember = (dept: any) => {
    setSelectedDepartment(dept);
    setIsModalOpen(true);
  };

  const handleSaveMembers = async () => {
    // 使用统一的刷新方法
    try {
      await refreshDepartmentData();
      showNotification({
        message: t('adminConsole.tip.update_success'),
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to refresh department data:', error);
      showNotification({
        message: t('adminConsole.tip.update_error'),
        color: 'red',
      });
    }
  };

  const handleDeleteDepartment = async (deptId: string) => {
    try {
      await deleteDepartment({ id: deptId });
      // 使用统一的刷新方法
      await refreshDepartmentData();
      showNotification({
        message: t('adminConsole.tip.delete_success'),
        color: 'green',
      });
    } catch (error) {
      console.error(t('adminConsole.departmentTable.deleteError'), error);
      showNotification({
        message: t('adminConsole.tip.delete_error'),
        color: 'red',
      });
    }
  };

  const confirmDeleteDepartment = (deptId: string) => {
    setDepartmentToDelete(deptId);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (departmentToDelete) {
      handleDeleteDepartment(departmentToDelete);
      setDepartmentToDelete(null);
    }
    setShowDeleteConfirmModal(false);
  };

  const renderDepartmentRow = (dept: any, level = 0) => {
    const isExpanded = expandedDepts.has(dept?.id);
    const hasContent =
      (Array.isArray(dept.members) && dept.members.length > 0) ||
      (Array.isArray(dept.children) && dept.children.length > 0);

    return (
      <div key={dept.id}>
        <div
          className={`
            flex items-center px-8 py-5 hover:bg-gray-50 group
            ${level > 0 ? 'border-l border-gray-200' : ''}
            transition-colors duration-150
          `}
          style={{ paddingLeft: `${level * 2 + 2}rem` }}
        >
          <div className="flex items-center">
            {hasContent ? (
              <button
                onClick={() => toggleDepartment(dept?.id || '')}
                className="p-1 rounded-full hover:bg-gray-100 transition-transform duration-150"
                style={{
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}
              >
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              </button>
            ) : (
              <div className="w-6 h-6" />
            )}
          </div>

          <div className="flex-1 flex items-center gap-6">
            <div className="flex items-center gap-4 min-w-[280px] mr-10 max-w-[400px] overflow-hidden">
              <div className="flex-1 overflow-hidden whitespace-nowrap text-ellipsis">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap">
                    {dept?.name || 'Unknown Department'}
                  </span>
                  {dept?.code && (
                    <span className="text-xs text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap">
                      ({dept.code})
                    </span>
                  )}
                </div>
                {dept?.description && (
                  <div className="text-xs text-gray-500 mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
                    {dept.description}
                  </div>
                )}
              </div>
            </div>
            {dept?.totalMembers > 0 && (
              <div className="flex items-center gap-3 min-w-[180px]">
                <span className="text-sm text-gray-500">
                  {dept.totalMembers}
                  {dept.totalMembers === 1
                    ? t('adminConsole.departmentTable.member')
                    : t('adminConsole.departmentTable.members')}
                </span>
              </div>
            )}
          </div>
          <div
            className="flex items-center gap-2 opacity-0 group-hover:opacity-100
            transition-opacity duration-150 pr-2"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAddMember(dept);
              }}
              className="p-2 text-gray-400 hover:text-primary-600
                rounded-lg hover:bg-gray-100 transition-colors duration-150"
              title={t('adminConsole.departmentTable.addMember')}
            >
              <UserPlusIcon className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddDepartment(dept?.id || '');
              }}
              className="p-2 text-gray-400 hover:text-primary-600
                rounded-lg hover:bg-gray-100 transition-colors duration-150"
              title={t('adminConsole.departmentTable.addSubDepartment')}
            >
              <PlusCircleIcon className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditDepartment(dept);
              }}
              className="p-2 text-gray-400 hover:text-gray-600
                rounded-lg hover:bg-gray-100 transition-colors duration-150"
              title={t('adminConsole.departmentTable.editDepartment')}
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
            {!hasContent && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  confirmDeleteDepartment(dept?.id || '');
                }}
                className="p-2 text-gray-400 hover:text-red-600
                  rounded-lg hover:bg-gray-100 transition-colors duration-150"
                title={t('adminConsole.departmentTable.deleteDepartment')}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
        <div
          className="overflow-hidden transition-all duration-200 ease-in-out"
          style={{
            maxHeight: isExpanded ? '9999px' : '0',
            opacity: isExpanded ? 1 : 0,
          }}
        >
          {Array.isArray(dept.members) && dept.members.length > 0 && (
            <div
              className="py-3 px-8 bg-gray-50 border-y border-gray-100"
              style={{ paddingLeft: `${level * 2 + 2}rem` }}
            >
              <div className="space-y-2">
                {dept.members.map((member: any) => (
                  <div
                    key={member?.id || Math.random()}
                    className="flex items-center gap-3 py-1.5 px-4 rounded-lg
                      hover:bg-white transition-colors duration-150"
                  >
                    <Avatar
                      src={member?.avatar || null}
                      alt={member?.name || 'Unknown'}
                      className="w-8 h-8 rounded-full"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{member?.name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{member?.email || 'No email'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(dept.children) && dept.children.length > 0 && (
            <div>{dept.children.map((child: any) => renderDepartmentRow(child, level + 1))}</div>
          )}
        </div>
      </div>
    );
  };
  return (
    <div className="divide-y divide-gray-200" style={{ overflowY: 'auto' }}>
      <div className="bg-gray-50">
        <div className="flex items-center px-8 py-6">
          <div className="flex-1 flex items-center gap-6">
            <div className="flex items-center gap-4 min-w-[280px] mr-10" style={{ maxWidth: '50%' }}>
              <img
                src="https://api.dicebear.com/7.x/identicon/svg?seed=acme"
                alt={companyName || 'Company'}
                className="w-10 h-10 rounded-lg bg-white p-2 shadow-sm"
              />
              <div>
                <div className="text-lg font-semibold text-gray-900">{companyName || 'Unknown Company'}</div>
                <div className="text-sm text-gray-700">{companyDescription}</div>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <div className="mr-4">
                {Array.isArray(departmentList) ? departmentList.length : 0}{' '}
                {Array.isArray(departmentList) && departmentList.length === 1
                  ? t('adminConsole.departmentTable.department')
                  : t('adminConsole.departmentTable.departments')}
              </div>
              <div>
                {Array.isArray(departmentList) ? getTotalMembers(departmentList) : 0}{' '}
                {t('adminConsole.departmentTable.totalMembers')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onAddDepartment('')}
              className="p-2 text-sm text-gray-600 hover:text-gray-700
                bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-150"
            >
              {t('adminConsole.departmentTable.addDepartment')}
            </button>
            <button
              onClick={() => onEditCompany()}
              className="p-1.5 text-gray-400 hover:text-gray-600
                rounded-lg hover:bg-gray-100 transition-colors duration-150"
              title={t('adminConsole.departmentTable.editCompanySettings')}
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <MemberTransferModal
          opened={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedDepartment={selectedDepartment || { id: '', members: [] }}
          onSave={handleSaveMembers}
        />
      )}
      <DeleteConfirmModal
        opened={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        mode="delete"
      />
      <div>{Array.isArray(departmentList) && departmentList.map((dept: any) => renderDepartmentRow(dept, 0))}</div>
    </div>
  );
}

function getTotalMembers(departments: any[]) {
  // 确保departments是有效的数组
  if (!departments || !Array.isArray(departments) || departments.length === 0) {
    return 0;
  }

  return departments.reduce((total, dept) => {
    // 确保dept存在且有效
    if (!dept) {
      return total;
    }

    const memberCount = Array.isArray(dept.members) ? dept.members.length : 0;
    const childrenMembers = dept.children ? getTotalMembers(dept.children) : 0;
    return total + memberCount + childrenMembers;
  }, 0);
}
